import { useState } from 'react'

import { Copy } from '@/components/base/Copy'
import { cn } from '@/utils/style'

type Shell = 'posix' | 'powershell' | 'cmd'

interface ApiAccessExampleProps {
  url: string
  model: string
  protocol: 'openai' | 'claude'
}

/** Vesper palette (per design): command/method peach, flags muted, strings mint. */
const TOKEN_CLASSES = {
  cmd: 'text-[#FFC799]',
  flag: 'text-[#A0A0A0]',
  str: 'text-[#99FFE4]',
  plain: 'text-white',
} as const

type TokenType = keyof typeof TOKEN_CLASSES

interface Token {
  text: string
  type: TokenType
}

const SHELL_TABS: { key: Shell; label: string }[] = [
  { key: 'posix', label: 'Mac/Linux' },
  { key: 'powershell', label: 'Windows (PowerShell)' },
  { key: 'cmd', label: 'Windows (CMD)' },
]

const CONTINUATION: Record<Shell, string> = {
  posix: ' \\\n  ',
  powershell: ' `\n  ',
  cmd: ' ^\n  ',
}

function buildTokens(shell: Shell, { url, model, protocol }: ApiAccessExampleProps): Token[] {
  const cont = CONTINUATION[shell]

  // PowerShell body must contain NO whitespace: Windows PowerShell 5.1 only
  // re-quotes native args that contain spaces, so a spaced JSON body reaches
  // curl.exe split into several arguments.
  const content = shell === 'powershell' ? 'hi' : 'Say hi in 3 words'

  // Anthropic Messages requires max_tokens; OpenAI Chat Completions does not.
  const json =
    protocol === 'claude'
      ? `{"model":"${model}","max_tokens":1024,"messages":[{"role":"user","content":"${content}"}]}`
      : `{"model":"${model}","messages":[{"role":"user","content":"${content}"}]}`

  // On Windows the example must call curl.exe (in PowerShell bare `curl`
  // aliases Invoke-WebRequest), and the JSON's own quotes must be escaped as
  // \" for the Windows native-arg parser — bare ones get stripped. CMD has no
  // single-quote string, so there the body is wrapped in double quotes.
  // Note: the \" form targets Windows PowerShell 5.1 (the stock powershell.exe);
  // pwsh 7.3+ changed native-arg passing and double-escapes it.
  const curl = shell === 'posix' ? 'curl' : 'curl.exe'
  const body =
    shell === 'posix'
      ? `'${json}'`
      : shell === 'powershell'
        ? `'${json.replace(/"/g, '\\"')}'`
        : `"${json.replace(/"/g, '\\"')}"`

  return [
    { text: curl, type: 'cmd' },
    { text: ' -X ', type: 'flag' },
    { text: 'POST', type: 'cmd' },
    { text: ` ${url}`, type: 'plain' },
    { text: cont, type: 'flag' },
    { text: '-H ', type: 'flag' },
    { text: '"Authorization: Bearer YOUR_API_KEY"', type: 'str' },
    { text: cont, type: 'flag' },
    { text: '-H ', type: 'flag' },
    { text: '"Content-Type: application/json"', type: 'str' },
    { text: cont, type: 'flag' },
    { text: '-d ', type: 'flag' },
    { text: body, type: 'str' },
  ]
}

/** Dark runnable-curl example block with per-shell tabs and copy (Vesper-themed). */
export function ApiAccessExample(props: ApiAccessExampleProps) {
  const [shell, setShell] = useState<Shell>('posix')

  const tokens = buildTokens(shell, props)
  const plainText = tokens.map((token) => token.text).join('')

  return (
    <div className="rounded-lg overflow-hidden bg-[#101010]">
      <div className="flex items-center justify-between px-4 bg-[#3A3A3A]">
        <div className="flex gap-4">
          {SHELL_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={cn(
                'py-2.5 t-xs border-b-2 -mb-px cursor-pointer',
                shell === tab.key
                  ? 'text-white border-white font-bold'
                  : 'text-[#A0A0A0] border-transparent hover:text-white',
              )}
              onClick={() => setShell(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Copy text={plainText} size="md" />
      </div>
      <pre className="px-4 py-3 t-xs leading-5 overflow-x-auto">
        <code>
          {tokens.map((token, index) => (
            <span key={index} className={TOKEN_CLASSES[token.type]}>
              {token.text}
            </span>
          ))}
        </code>
      </pre>
    </div>
  )
}
